import { useParams, useNavigate } from 'react-router';
import { Header } from '@amzn/awsui-components-console';
import type { RuleDetails } from '@amzn/helianthus-arbiter-client';
import { ConflictException } from '@amzn/helianthus-arbiter-client';
import { useArbiterClient } from '../../context/ArbiterClientContext';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useRule } from '../../hooks/useRule';
import { ArnDisplay } from '../../components/ArnDisplay';
import { LoadingState } from '../../components/LoadingState';
import { ProvenanceAlert } from '../../components/ProvenanceAlert';
import { buildRuleUrl } from '../../utils/evaluationHelpers';
import { RuleForm, type RuleFormValues } from './RuleForm';
import { RuleLoadError } from './RuleLoadError';

export function EditRulePage() {
  const { ruleName } = useParams<{ ruleName: string }>();
  useDocumentTitle(ruleName ? `Edit Rule: ${ruleName}` : 'Edit Rule');
  const { rule, loading, error } = useRule(ruleName);

  if (loading) return <LoadingState label="Loading rule..." />;
  if (error || !rule) return <RuleLoadError ruleName={ruleName} error={error} />;
  return <EditRuleForm rule={rule} />;
}

function EditRuleForm({ rule }: { rule: RuleDetails }) {
  const navigate = useNavigate();
  const client = useArbiterClient();
  const ruleUrl = buildRuleUrl(rule.ruleName!);

  const handleSubmit = async (values: RuleFormValues) => {
    try {
      await client.updateRule({
        ...values,
        expectedVersion: rule.canonicalVersion,
      });
    } catch (err) {
      if (err instanceof ConflictException) {
        throw new Error(`${err.message} Reload the page to edit the latest version.`);
      }
      throw err;
    }
    navigate(ruleUrl);
  };

  return (
    <RuleForm
      initialValues={rule}
      header={
        <Header
          variant="h1"
          description={
            <>
              Editing version {rule.canonicalVersion}; saving creates version {Number(rule.canonicalVersion) + 1}.
              {' '}Last modified by <ArnDisplay value={rule.lastModifiedBy} />.
            </>
          }
        >
          Edit Rule: {rule.ruleName}
        </Header>
      }
      banner={<ProvenanceAlert rule={rule} />}
      submitLabel="Save changes"
      onSubmit={handleSubmit}
      onCancel={() => navigate(ruleUrl)}
    />
  );
}
